/**
 * The commission render decision, kept pure so it can be tested without a database.
 *
 * History, because the file name outlived its contents:
 *
 *   - It began as three per-tenant gates, each a boolean column on `tenant_settings`
 *     (`site_survey_pm_managed` and `lead_routing_edge_functions` from 20260805m, and
 *     LDG-1.2's commission column from 20260819a5).
 *   - RBAC-6.1 (20261102b8) made all three modules in the account registry and moved every
 *     reader onto `my_account_modules()`. The Site Survey and lead-routing helpers that lived
 *     here lost their last caller then.
 *   - RBAC-6.90 (20261103a6) dropped the three columns. Only the commission decision is left,
 *     and its input is the registry's answer for module `commission_costing`.
 *
 * CLOSED BY DEFAULT. When the answer is absent (no registry row, a fetch failure) the gate
 * reads FALSE. A gate must never open because data was missing.
 */

/**
 * What the commission render decision is made from. `commission_costing` is the registry
 * module's active flag, as `isModuleActive(modules, 'commission_costing')` reports it.
 */
export interface TenantGateSettings {
  commission_costing?: boolean | null;
}

/**
 * Whether commission tracking and job costing are live for this account.
 *
 * ⚠️ THIS DECIDES WHAT TO RENDER, AND NOTHING ELSE. It is not the gate. The gate
 * is the database function `commission_enabled(tenant_id)`, which triggers and
 * the joules-admin-backend workers call, because everything this feature writes
 * is automatic: a stage change fires a trigger, a worker derives a cost, a job
 * absorbs overhead. None of that runs in a browser, so a check here cannot stop
 * it, and a UI-only gate would let the system accumulate earning events and cost
 * lines across every account while appearing to be off. That is the failure
 * LDG-1.2 exists to prevent.
 *
 * Use this to decide whether a nav entry, tab, settings page or widget appears.
 * Never as the authority for a write.
 */
export function isCommissionCostingEnabled(
  settings: TenantGateSettings | null | undefined
): boolean {
  return settings?.commission_costing === true;
}
